if (!customElements.get('collection-infinite-scroll')) {
  const COLLECTION_SCROLL_STATE_PREFIX = 'afrihair-collection-state:';

  class CollectionInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.onIntersection = this.onIntersection.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.handleBackToTopClick = this.handleBackToTopClick.bind(this);
    }

    connectedCallback() {
      this.grid = this.querySelector('#product-grid');
      this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
      this.status = this.querySelector('[data-infinite-scroll-status]');
      this.paginationHost = this.querySelector('[data-pagination-wrapper]');
      this.backToTopButton = this.querySelector('[data-back-to-top]');

      if (!this.grid) return;

      this.isLoading = false;
      this.sectionId = this.dataset.sectionId || '';
      this.infiniteScrollEnabled = this.dataset.enableInfiniteScroll === 'true';
      this.supportsInfiniteScroll =
        this.infiniteScrollEnabled && 'IntersectionObserver' in window && 'fetch' in window;
      this.currentPage = this.getPageNumber(this.dataset.currentPage || window.location.href);
      this.activePage = this.getPageNumber(window.location.href);
      this.totalPages = parseInt(this.dataset.totalPages, 10) || this.currentPage;
      this.productsPerPage = parseInt(this.dataset.productsPerPage, 10) || this.grid.children.length || 16;
      this.storageKey = `${COLLECTION_SCROLL_STATE_PREFIX}${this.getStateKey()}`;
      this.lastHistoryUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      this.visiblePageMarkers = new Set();
      this.navigationType = performance.getEntriesByType('navigation')[0]?.type || 'navigate';
      this.shouldRestoreOnBack = false;

      this.setupBackToTop();
      this.grid.addEventListener('click', this.onGridClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);

      this.onScroll();
      this.refreshNextUrl();
      this.registerPageAnchor(this.grid.firstElementChild, this.currentPage);
      this.classList.toggle('collection-infinite-scroll--enabled', this.supportsInfiniteScroll);

      if (!this.supportsInfiniteScroll) return;

      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      this.setupPageObserver();
      this.setupObserver();
      this.restoreScrollState();

      if (this.navigationType !== 'back_forward' && this.currentPage > 1) {
        this.restoreEntryPageStack(this.currentPage);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.pageObserver?.disconnect();
      this.grid?.removeEventListener('click', this.onGridClick);
      this.backToTopButton?.removeEventListener('click', this.handleBackToTopClick);
      if (this.backToTopButton?.parentElement === document.body) {
        this.backToTopButton.remove();
      }
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    getStateKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return `${url.pathname}?${url.searchParams.toString()}`;
    }

    getPageNumber(source = window.location.href) {
      if (typeof source === 'string' && /^\d+$/.test(source)) return Number(source);

      const url = new URL(source, window.location.origin);
      const page = parseInt(url.searchParams.get('page'), 10);
      return Number.isNaN(page) || page < 1 ? 1 : page;
    }

    buildPageUrl(page) {
      const url = new URL(window.location.href);

      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', page);
      }

      return `${url.pathname}${url.search}${url.hash}`;
    }

    refreshNextUrl() {
      const nextLink =
        this.paginationHost?.querySelector('a[rel="next"]') ||
        this.paginationHost?.querySelector('a.pagination__item--prev') ||
        this.paginationHost?.querySelector('a[aria-label*="Next"], a[aria-label*="next"], a[aria-label*="Older"]');

      this.nextUrl = nextLink?.href || null;
      this.dataset.currentPage = String(this.currentPage);

      if (this.grid) {
        this.grid.dataset.currentPage = String(this.currentPage);
      }
    }

    setupObserver() {
      if (!this.sentinel || !this.nextUrl || this.currentPage >= this.totalPages) return;

      this.observer?.disconnect();
      this.observer = new IntersectionObserver(this.onIntersection, {
        rootMargin: '0px 0px 600px 0px',
      });
      this.observer.observe(this.sentinel);
    }

    setupBackToTop() {
      if (!this.backToTopButton) return;

      if (this.backToTopButton.parentElement !== document.body) {
        document.body.appendChild(this.backToTopButton);
      }

      this.backToTopButton.addEventListener('click', this.handleBackToTopClick);
    }

    handleBackToTopClick() {
      this.updateHistory(1);
      window.scrollTo({ top: 0, left: 0 });
    }

    onScroll() {
      const shouldShow = window.scrollY > 10 || this.activePage > 1 || this.getPageNumber(window.location.href) > 1;
      this.backToTopButton?.classList.toggle('is-visible', shouldShow);
    }

    setupPageObserver() {
      if (!this.grid || !('IntersectionObserver' in window)) return;

      this.pageObserver?.disconnect();
      this.visiblePageMarkers.clear();
      this.pageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const pageNumber = Number(entry.target.dataset.pageNumber);
          if (Number.isNaN(pageNumber)) return;

          if (entry.isIntersecting) {
            this.visiblePageMarkers.add(pageNumber);
          } else {
            this.visiblePageMarkers.delete(pageNumber);
          }
        });

        if (this.visiblePageMarkers.size === 0) return;

        const highestVisiblePage = Math.max(...this.visiblePageMarkers);
        this.updateHistory(highestVisiblePage);
      }, {
        rootMargin: '-10% 0px -55% 0px',
        threshold: 0.01,
      });

      this.grid.querySelectorAll('[data-page-anchor="true"]').forEach((marker) => {
        this.pageObserver.observe(marker);
      });
    }

    registerPageAnchor(element, pageNumber) {
      if (!element || !pageNumber) return;

      element.dataset.pageAnchor = 'true';
      element.dataset.pageNumber = String(pageNumber);

      if (this.pageObserver) {
        this.pageObserver.observe(element);
      }
    }

    onPageHide() {
      if (this.shouldRestoreOnBack) {
        this.persistState('product-click');
      }
    }

    onPageShow(event) {
      if (event.persisted) {
        this.onScroll();
      }
    }

    onGridClick(event) {
      const productLink = event.target.closest('.full-unstyled-link[href]');
      if (!productLink || !this.grid.contains(productLink)) return;

      this.shouldRestoreOnBack = true;
      this.persistState('product-click');
    }

    persistState(reason = 'product-click') {
      if (!this.grid) return;

      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            currentPage: this.currentPage,
            loadedPage: this.currentPage,
            activePage: this.activePage,
            itemCount: this.grid.children.length,
            scrollY: window.scrollY,
            reason,
            timestamp: Date.now(),
          })
        );
      } catch (error) {
        console.warn('Unable to persist collection scroll state', error);
      }
    }

    readState() {
      try {
        const rawState = sessionStorage.getItem(this.storageKey);
        return rawState ? JSON.parse(rawState) : null;
      } catch (error) {
        console.warn('Unable to read collection scroll state', error);
        return null;
      }
    }

    async restoreScrollState() {
      if (this.navigationType !== 'back_forward') {
        sessionStorage.removeItem(this.storageKey);
        return;
      }

      const savedState = this.readState();
      if (!savedState || savedState.reason !== 'product-click') return;

      const isFresh = Date.now() - Number(savedState.timestamp || 0) < 30 * 60 * 1000;
      if (!isFresh) {
        sessionStorage.removeItem(this.storageKey);
        return;
      }

      const savedLoadedPage = Number(savedState.loadedPage || savedState.currentPage || 1);
      const savedActivePage = Number(savedState.activePage || savedLoadedPage || 1);
      const shouldRebuild = savedLoadedPage > 1 && Number(savedState.itemCount || 0) > this.grid.children.length;

      if (shouldRebuild) {
        this.toggleStatus(true);

        try {
          await this.rebuildPages(savedLoadedPage);
        } catch (error) {
          console.warn('Unable to restore full collection state', error);
        } finally {
          this.toggleStatus(false);
        }
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, Number(savedState.scrollY) || 0);
          this.updateHistory(savedActivePage);
        });
      });

      sessionStorage.removeItem(this.storageKey);
    }

    async restoreEntryPageStack(targetPage) {
      if (!this.supportsInfiniteScroll || this.isLoading || targetPage <= 1) return;

      this.isLoading = true;
      this.toggleStatus(true);

      try {
        await this.rebuildPages(targetPage);

        requestAnimationFrame(() => {
          const targetAnchor = this.grid?.querySelector(`[data-page-anchor="true"][data-page-number="${targetPage}"]`);
          if (!targetAnchor) return;

          const stickyHeader =
            document.querySelector('sticky-header') ||
            document.querySelector('.shopify-section-header-sticky');
          const stickyOffset = stickyHeader?.offsetHeight || 0;
          const anchorTop = targetAnchor.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: Math.max(0, anchorTop - stickyOffset - 16),
            left: 0,
          });
        });
      } catch (error) {
        console.warn('Unable to restore earlier collection pages', error);
      } finally {
        this.isLoading = false;
        this.toggleStatus(false);
      }
    }

    async rebuildPages(targetPage) {
      if (targetPage <= 1) return;

      const firstPageDocument = await this.fetchCollectionDocument(this.buildPageUrl(1));
      this.replaceGrid(firstPageDocument.grid);
      this.updatePagination(firstPageDocument.pagination);
      this.currentPage = 1;
      this.refreshNextUrl();

      while (this.currentPage < targetPage && this.nextUrl) {
        const pageToLoad = this.currentPage + 1;
        const nextPageDocument = await this.fetchCollectionDocument(this.buildPageUrl(pageToLoad));

        this.appendGridItems(nextPageDocument.grid);
        this.updatePagination(nextPageDocument.pagination);
        this.currentPage = pageToLoad;
        this.refreshNextUrl();
      }

      this.updateHistory(targetPage);
      this.setupPageObserver();
      this.setupObserver();
    }

    async onIntersection(entries) {
      const [entry] = entries;
      if (!entry?.isIntersecting || !this.nextUrl || this.isLoading) return;

      await this.loadNextPage();
    }

    async loadNextPage() {
      this.isLoading = true;
      this.toggleStatus(true);
      const requestUrl = this.nextUrl;

      try {
        const nextPageDocument = await this.fetchCollectionDocument(requestUrl);
        const newPage = nextPageDocument.page || this.currentPage + 1;

        this.currentPage = Math.max(this.currentPage, newPage);
        this.appendGridItems(nextPageDocument.grid, newPage);
        this.updatePagination(nextPageDocument.pagination);
        this.refreshNextUrl();
        this.persistState();

        if (!this.nextUrl || this.currentPage >= this.totalPages) {
          this.observer?.disconnect();
        }
      } catch (error) {
        console.error('Infinite scroll request failed', error);
        this.observer?.disconnect();
      } finally {
        this.isLoading = false;
        this.toggleStatus(false);
      }
    }

    getFetchUrl(url) {
      const requestUrl = new URL(url, window.location.origin);

      if (this.sectionId) {
        requestUrl.searchParams.set('section_id', this.sectionId);
      }

      return requestUrl.toString();
    }

    async fetchCollectionDocument(url) {
      const fetchUrl = this.getFetchUrl(url);
      const response = await fetch(fetchUrl, {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      let html;

      const responseClone = response.clone();

      try {
        const json = await response.json();
        if (this.sectionId && json.sections?.[this.sectionId]) {
          html = json.sections[this.sectionId];
        } else if (json.content) {
          html = json.content;
        } else if (this.sectionId) {
          throw new Error(`Section "${this.sectionId}" not found in JSON response`);
        } else {
          throw new Error('No HTML content found in JSON response');
        }
      } catch (parseError) {
        if (parseError.message.includes('not found')) {
          throw parseError;
        }
        html = await responseClone.text();
      }

      const responseDocument = new DOMParser().parseFromString(html, 'text/html');
      const grid = responseDocument.getElementById('product-grid');

      if (!grid) {
        console.error('[InfiniteScroll] Grid not found. Response preview:', html.substring(0, 200));
        throw new Error('Product grid not found in fetched HTML');
      }

      const pagination = responseDocument.querySelector('[data-pagination-wrapper]');
      const page = this.getPageNumber(url);

      return { grid, pagination, page };
    }

    replaceGrid(incomingGrid, pageNumber = 1) {
      this.grid.innerHTML = '';

      const items = Array.from(incomingGrid.children);
      items.forEach((item) => {
        this.prepareCardMedia(item);
        this.grid.appendChild(item);
      });

      this.registerPageAnchor(this.grid.firstElementChild, pageNumber);
      this.reinitializeAnimations();
    }

    appendGridItems(incomingGrid, pageNumber = this.currentPage + 1) {
      const items = Array.from(incomingGrid.children);

      items.forEach((item) => {
        this.prepareCardMedia(item);
        this.grid.appendChild(item);
      });

      this.registerPageAnchor(items[0], pageNumber);
      this.reinitializeAnimations();
    }

    updatePagination(incomingPagination) {
      if (!this.paginationHost) return;
      this.paginationHost.innerHTML = incomingPagination ? incomingPagination.innerHTML : '';
    }

    prepareCardMedia(scope) {
      scope.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';

        if ('fetchPriority' in image) {
          image.fetchPriority = 'low';
        }
      });
    }

    toggleStatus(isVisible) {
      if (!this.status) return;

      this.status.hidden = !isVisible;
      this.status.setAttribute('aria-busy', isVisible ? 'true' : 'false');
    }

    updateHistory(pageNumber) {
      const normalizedPage = Math.max(1, Math.min(Number(pageNumber) || 1, this.totalPages));
      const nextUrl = new URL(window.location.href);

      if (normalizedPage <= 1) {
        nextUrl.searchParams.delete('page');
      } else {
        nextUrl.searchParams.set('page', normalizedPage);
      }

      const nextUrlString = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

      if (this.activePage === normalizedPage && this.lastHistoryUrl === nextUrlString) return;

      this.activePage = normalizedPage;
      this.lastHistoryUrl = nextUrlString;

      window.history.replaceState(
        { ...(window.history.state || {}), page: normalizedPage },
        '',
        nextUrlString
      );

      this.backToTopButton?.classList.toggle('is-visible', window.scrollY > 10 || normalizedPage > 1);
    }

    reinitializeAnimations() {
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(this.grid);
      }
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
